import type { CSSProperties, FocusEvent, ReactNode, TextareaHTMLAttributes } from 'react';
import {
    forwardRef,
    useCallback,
    useEffect,
    useId,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { cn } from '../../../utils/classname';
import './TextArea.scss';

type TextAreaSize = 'sm' | 'default' | 'lg' | 'small' | 'medium' | 'large' | 'md';

type ResolvedTextAreaSize = 'sm' | 'default' | 'lg';

const sizeStyles: Record<
    ResolvedTextAreaSize,
    Pick<CSSProperties, 'padding' | 'fontSize' | 'minHeight'>
> = {
    sm: {
        padding: '0.25rem 0.5rem',
        fontSize: '0.8125rem',
        minHeight: '2rem',
    },
    default: {
        padding: '0.335rem 0.65rem',
        fontSize: '0.875rem',
        minHeight: '2.5rem',
    },
    lg: {
        padding: '0.5rem 0.9rem',
        fontSize: '1rem',
        minHeight: '3rem',
    },
};

function resolveTextAreaSize(size: TextAreaSize): ResolvedTextAreaSize {
    if (size === 'small') return 'sm';
    if (size === 'medium' || size === 'md') return 'default';
    if (size === 'large') return 'lg';
    return size;
}

export interface ITextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
    size?: TextAreaSize;

    label?: ReactNode;
    labelClassName?: string;
    labelStyle?: CSSProperties;
    labelRight?: ReactNode;

    leftIcon?: ReactNode;
    rightIcon?: ReactNode;

    borderRadius?: CSSProperties['borderRadius'];
    borderWidth?: CSSProperties['borderWidth'];
    borderColor?: CSSProperties['borderColor'];
    borderStyle?: CSSProperties['borderStyle'];

    bgColor?: CSSProperties['backgroundColor'];
    backgroundColor?: CSSProperties['backgroundColor'];
    textColor?: CSSProperties['color'];
    placeholderColor?: CSSProperties['color'];
    focusBorderColor?: CSSProperties['borderColor'];

    padding?: CSSProperties['padding'];
    margin?: CSSProperties['margin'];
    gap?: CSSProperties['gap'];

    width?: CSSProperties['width'];
    height?: CSSProperties['height'];
    minHeight?: CSSProperties['minHeight'];
    maxHeight?: CSSProperties['maxHeight'];

    fontSize?: CSSProperties['fontSize'];
    fontWeight?: CSSProperties['fontWeight'];

    resize?: 'none' | 'both' | 'horizontal' | 'vertical';

    minRows?: number;
    maxRows?: number;
    autosize?: boolean;

    error?: ReactNode;

    description?: ReactNode;
    descriptionBottom?: ReactNode;

    containerClassName?: string;
    containerStyle?: CSSProperties;
    inputClassName?: string;
}

const TextArea = forwardRef<HTMLTextAreaElement, ITextAreaProps>(function TextArea(
    {
        size = 'default',
        label,
        labelClassName,
        labelStyle,
        labelRight,
        leftIcon,
        rightIcon,
        className,
        style,
        borderRadius = '6px',
        borderWidth = '1px',
        borderColor,
        borderStyle = 'solid',
        bgColor,
        backgroundColor,
        textColor,
        placeholderColor,
        focusBorderColor,
        padding,
        margin,
        gap,
        width,
        height,
        minHeight,
        maxHeight,
        fontSize,
        fontWeight,
        resize = 'vertical',
        rows = 4,
        minRows,
        maxRows,
        autosize = false,
        containerClassName,
        containerStyle,
        inputClassName,
        error,
        description,
        descriptionBottom,
        onFocus,
        onBlur,
        onChange,
        disabled,
        value,
        defaultValue,
        id: providedId,
        'aria-describedby': providedDescribedBy,
        ...textareaProps
    },
    ref
) {
    const autoId = useId();
    const textareaId = providedId ?? autoId;
    const descriptionId = `${textareaId}-description`;
    const descriptionBottomId = `${textareaId}-description-bottom`;
    const errorId = `${textareaId}-error`;
    const describedBy =
        [
            providedDescribedBy,
            description ? descriptionId : null,
            descriptionBottom ? descriptionBottomId : null,
            error ? errorId : null,
        ]
            .filter(Boolean)
            .join(' ') || undefined;

    const [focused, setFocused] = useState(false);
    const [announceError, setAnnounceError] = useState(false);
    const innerRef = useRef<HTMLTextAreaElement | null>(null);

    useEffect(() => setAnnounceError(focused), [focused]);

    useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement, []);

    const sizeStyle = sizeStyles[resolveTextAreaSize(size)];

    const resolvedPadding = padding ?? sizeStyle.padding;
    const resolvedFontSize = fontSize ?? sizeStyle.fontSize;
    const resolvedMinHeight = minHeight ?? sizeStyle.minHeight;

    const resolvedBackgroundColor =
        bgColor ??
        backgroundColor ??
        'var(--color-input-bg, color-mix(in srgb, var(--color-background) 97%, var(--color-button-bg)))';

    const resolvedTextColor = textColor ?? 'var(--color-text, currentColor)';
    const resolvedBorderColor = borderColor ?? 'var(--color-input-border, var(--color-border))';
    const resolvedFocusBorderColor = focusBorderColor ?? 'var(--color-accent)';

    const adjustHeight = useCallback(() => {
        const el = innerRef.current;
        if (!el || !autosize) return;

        const computed = window.getComputedStyle(el);
        const lineHeight = parseFloat(computed.lineHeight) || parseFloat(computed.fontSize) * 1.2;
        const paddingTop = parseFloat(computed.paddingTop) || 0;
        const paddingBottom = parseFloat(computed.paddingBottom) || 0;
        const borderTop = parseFloat(computed.borderTopWidth) || 0;
        const borderBottom = parseFloat(computed.borderBottomWidth) || 0;
        const verticalSpacing = paddingTop + paddingBottom + borderTop + borderBottom;

        el.style.height = 'auto';
        let next = el.scrollHeight;

        if (minRows) {
            const minPx = lineHeight * minRows + verticalSpacing;
            if (next < minPx) next = minPx;
        }
        if (maxRows) {
            const maxPx = lineHeight * maxRows + verticalSpacing;
            if (next > maxPx) {
                next = maxPx;
                el.style.overflowY = 'auto';
            } else {
                el.style.overflowY = 'hidden';
            }
        } else {
            el.style.overflowY = 'hidden';
        }

        el.style.height = `${next}px`;
    }, [autosize, minRows, maxRows]);

    useEffect(() => {
        if (autosize) adjustHeight();
    }, [autosize, adjustHeight, value, defaultValue]);

    useEffect(() => {
        const el = innerRef.current;
        if (!el || autosize) return;

        const computed = window.getComputedStyle(el);
        const lineHeight = parseFloat(computed.lineHeight) || parseFloat(computed.fontSize) * 1.5;

        el.style.minHeight = minRows ? `${lineHeight * minRows}px` : '';
        el.style.maxHeight = maxRows ? `${lineHeight * maxRows}px` : '';
    }, [autosize, minRows, maxRows, fontSize, size]);

    const inputBaseStyle: CSSProperties & {
        '--input-placeholder-color'?: string;
    } = {
        flex: 1,
        width: '100%',
        minWidth: 0,
        outline: 'none',
        border: 'none',
        background: 'transparent',
        color: resolvedTextColor,
        fontSize: resolvedFontSize,
        fontWeight,
        fontFamily: 'inherit',
        lineHeight: 1.5,
        cursor: disabled ? 'not-allowed' : 'text',
        opacity: disabled ? 0.6 : 1,
        resize: autosize ? 'none' : resize,
        ...(placeholderColor ? { '--input-placeholder-color': placeholderColor } : {}),
    };

    const containerStyleResolved: CSSProperties = {
        display: 'inline-flex',
        alignItems: 'flex-start',
        position: 'relative',
        width: width ?? '100%',
        height,
        minHeight: resolvedMinHeight,
        maxHeight,
        margin,
        gap: gap ?? '0.35rem',
        padding: resolvedPadding,
        borderRadius,
        borderWidth,
        borderStyle,
        borderColor: error
            ? 'var(--color-error, #ef4444)'
            : focused
              ? resolvedFocusBorderColor
              : resolvedBorderColor,
        backgroundColor: resolvedBackgroundColor,
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        boxShadow: error
            ? `0 0 0 2px color-mix(in srgb, var(--color-error, #ef4444) 30%, transparent)`
            : focused
              ? `0 0 0 2px color-mix(in srgb, ${resolvedFocusBorderColor} 30%, transparent)`
              : undefined,
        opacity: disabled ? 0.7 : undefined,
        ...containerStyle,
    };

    const handleFocus = useCallback(
        (event: FocusEvent<HTMLTextAreaElement>) => {
            setFocused(true);
            onFocus?.(event);
        },
        [onFocus]
    );

    const handleBlur = useCallback(
        (event: FocusEvent<HTMLTextAreaElement>) => {
            setFocused(false);
            onBlur?.(event);
        },
        [onBlur]
    );

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange?.(event);
            if (autosize) adjustHeight();
        },
        [onChange, autosize, adjustHeight]
    );

    const labelElement = label ? (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.25rem',
                gap: '0.5rem',
            }}
        >
            <label
                htmlFor={textareaId}
                className={cn('xv-textarea-label', labelClassName)}
                style={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'var(--color-text, currentColor)',
                    ...labelStyle,
                }}
            >
                {label}
            </label>
            {labelRight ? (
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>{labelRight}</span>
            ) : null}
        </div>
    ) : null;

    return (
        <div
            className={cn('xv-textarea-root', containerClassName)}
            style={{ display: 'inline-flex', flexDirection: 'column', width: width ?? '100%' }}
        >
            {labelElement}
            {description && (
                <span
                    id={descriptionId}
                    className="xv-textarea-description"
                    style={{
                        fontSize: '0.875rem',
                        color: 'color-mix(in srgb, var(--color-text) 68%, transparent)',
                        marginBottom: '0.25rem',
                    }}
                >
                    {description}
                </span>
            )}
            <div className={cn('xv-textarea-wrapper', className)} style={containerStyleResolved}>
                {leftIcon && (
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'flex-start',
                            flexShrink: 0,
                            paddingTop: '0.15rem',
                            color: 'var(--color-text-muted, currentColor)',
                        }}
                    >
                        {leftIcon}
                    </span>
                )}
                <textarea
                    ref={innerRef}
                    rows={rows}
                    disabled={disabled}
                    value={value}
                    defaultValue={defaultValue}
                    className={cn('xv-textarea', inputClassName)}
                    style={{ ...inputBaseStyle, ...style }}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    {...textareaProps}
                    id={textareaId}
                    aria-invalid={error ? true : textareaProps['aria-invalid']}
                    aria-describedby={describedBy}
                />
                {rightIcon && (
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'flex-start',
                            flexShrink: 0,
                            paddingTop: '0.15rem',
                            color: 'var(--color-text-muted, currentColor)',
                        }}
                    >
                        {rightIcon}
                    </span>
                )}
            </div>
            {descriptionBottom && (
                <span
                    id={descriptionBottomId}
                    className="xv-textarea-description-bottom"
                    style={{
                        fontSize: '0.75rem',
                        color: 'color-mix(in srgb, var(--color-text) 68%, transparent)',
                        marginTop: '0.25rem',
                    }}
                >
                    {descriptionBottom}
                </span>
            )}
            <span
                id={errorId}
                aria-live={announceError ? 'polite' : 'off'}
                style={{
                    fontSize: '0.75rem',
                    color: 'var(--color-error, #ef4444)',
                    marginTop: error ? '0.25rem' : undefined,
                }}
            >
                {error}
            </span>
        </div>
    );
});

export default TextArea;
